import { useMutation } from '@tanstack/react-query'

import type { AccountIdentity } from '@/bindings/AccountIdentity'
import type { AccountLookupRequest } from '@/bindings/AccountLookupRequest'
import type { LoginIdentifier } from '@/bindings/LoginIdentifier'
import { Badge } from '@/components/badge'
import { type IdentifierDraft, identifierEdited, toLoginIdentifier } from '@/features/auth/identifier-draft'
import { IdentifierSearch } from '@/features/auth/identifier-search'
import { Identity } from '@/features/back-office/account-parts'
import { request } from '@/lib/api'
import { useT } from '@/lib/i18n/translate'

/** A looked-up account with the identifier it was reached by — what the link request sends. */
export interface SuperuserPick {
  user: AccountIdentity
  identifier: LoginIdentifier
}

/**
 * Search-then-pick for the superuser (platform create/edit), so an unregistered
 * owner is caught before the host form is sent. Editing the text drops the pick.
 */
export function SuperuserPicker({
  draft,
  onDraftChange,
  pick,
  onPick,
  searchFirst = false,
}: {
  draft: IdentifierDraft
  onDraftChange: (draft: IdentifierDraft) => void
  pick: SuperuserPick | undefined
  onPick: (pick: SuperuserPick | undefined) => void
  /** The host blocked its submit on an unsearched identifier; shown unless the search itself failed. */
  searchFirst?: boolean
}) {
  const t = useT()
  const lookup = useMutation({
    mutationFn: (identifier: LoginIdentifier) => {
      const body: AccountLookupRequest = { identifier }
      return request<AccountIdentity>('/platform/accounts/lookup', { method: 'POST', body })
    },
  })

  function search() {
    onPick(undefined)
    const identifier = toLoginIdentifier(draft)
    // Call-level: a search voided by an edit (`reset`) or superseded by a newer one never picks.
    lookup.mutate(identifier, { onSuccess: (user) => onPick({ user, identifier }) })
  }

  return (
    <div className="flex flex-col gap-4">
      <IdentifierSearch
        draft={draft}
        onDraftChange={(next) => {
          onDraftChange(next)
          // A channel toggle keeps the pick: it is tied to the identifier it was found by.
          if (identifierEdited(draft, next)) onPick(undefined)
          lookup.reset()
        }}
        onSearch={search}
        pending={lookup.isPending}
        error={lookup.error}
        required={false}
        variant="secondary"
      />
      {searchFirst && !lookup.error && (
        <p role="alert" className="text-danger text-sm">
          {t('restaurants.searchSuperuserFirst')}
        </p>
      )}
      {pick && (
        <div role="status" className="border-border flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
          <Identity user={pick.user} />
          <Badge tone="success">{t('restaurants.accountFound')}</Badge>
        </div>
      )}
    </div>
  )
}
